import { AlertCircleIcon, MailIcon } from 'lucide-react';
import { TextAreaField, TextField } from '../Field';
import { ResearchTypeTable } from './ResearchTypeTable';
import { GanttTable } from './GanttTable';
import { CoInvestigators } from './CoInvestigators';
import { DocumentsTable } from './DocumentsTable';
import { CoverLetterUpload } from './CoverLetterUpload';
import { usePortal } from '../../contexts/PortalContext';
import { INSTITUTION, IRB_EMAIL, REVIEW_CATEGORIES } from '../../data/proposalContent';
import { ganttMonthCount } from '../../data/fieldMap';
import type { WizardStep } from '../../data/wizardSteps';
import type { StepErrors } from '../../utils/validation';

interface StepContentProps {
  step: WizardStep;
  errors: StepErrors;
}

function InlineError({ message }: {message?: string;}) {
  if (!message) return null;
  return (
    <p className="error-text" role="alert">
      <AlertCircleIcon size={13} aria-hidden="true" />
      {message}
    </p>);

}

export function StepContent({ step, errors }: StepContentProps) {
  const { config, data, update, setNarrative } = usePortal();
  const irbEmail = config?.irbEmail ?? IRB_EMAIL;
  const maxMonths = config?.maxDurationMonths ?? 24;

  switch (step.kind) {
    case 'identification':
      return (
        <section className="card">
          <div className="card-body">
            <TextAreaField
              id="title"
              label="Title of proposal"
              required
              rows={3}
              value={data.title}
              onChange={(v) => update({ title: v })}
              error={errors.title} />
            

            <div className="field" style={{ marginTop: 'var(--space-5)' }}>
              <span className="field-label" id="review-category-label">
                Research proposal review category
              </span>
              <div
                className="choice-row"
                role="radiogroup"
                aria-labelledby="review-category-label">
                
                {REVIEW_CATEGORIES.map((category) =>
                <label
                  key={category}
                  className={`choice${data.reviewCategory === category ? ' choice--active' : ''}`}>
                  
                    <input
                    type="radio"
                    name="reviewCategory"
                    value={category}
                    checked={data.reviewCategory === category}
                    onChange={() => update({ reviewCategory: category })} />
                  
                    {category}
                  </label>
                )}
              </div>
              <InlineError message={errors.reviewCategory} />
            </div>
          </div>
        </section>);


    case 'projectTypes':
      return (
        <section className="card">
          <div className="card-header">
            <div>
              <h2 className="card-title">Type of Research Project</h2>
              <p className="card-subtitle">Please check all applicable options.</p>
            </div>
            <span className="badge badge--blue">{data.projectTypes.length} selected</span>
          </div>
          <div className="card-body">
            <ResearchTypeTable
              types={config?.researchTypes ?? []}
              selected={data.projectTypes}
              onToggle={(id) =>
                update({
                  projectTypes: data.projectTypes.includes(id)
                    ? data.projectTypes.filter((t) => t !== id)
                    : [...data.projectTypes, id]
                })
              }
            />
            
            <InlineError message={errors.projectTypes} />
          </div>
        </section>);


    case 'studyPeriod':
      return (
        <section className="card">
          <div className="card-body">
            <div className="grid-2">
              <TextField
                id="startDate"
                label="Proposed Starting Date"
                required
                type="date"
                value={data.startDate}
                onChange={(v) => update({ startDate: v })}
                error={errors.startDate} />
              
              <TextField
                id="durationMonths"
                label={`Duration of Research Project (in months upto ${maxMonths} maximum)`}
                required
                type="number"
                min="1"
                max={String(maxMonths)}
                value={data.durationMonths}
                onChange={(v) => update({ durationMonths: v })}
                error={errors.durationMonths} />
              
            </div>
          </div>
        </section>);


    case 'text':
      return (
        <section className="card">
          <div className="card-body stack">
            {(step.textareas ?? []).map((area) => {
              const value =
              area.source === 'narrative' ?
              data.narratives[area.key] ?? '' :
              (data as unknown as Record<string, string>)[area.key] ?? '';
              return (
                <TextAreaField
                  key={area.key}
                  id={area.key}
                  label={area.label}
                  hint={area.hint}
                  points={area.points}
                  rows={area.rows ?? 5}
                  required={!area.optional}
                  value={value}
                  onChange={(v) =>
                  area.source === 'narrative' ?
                  setNarrative(area.key, v) :
                  update({ [area.key]: v } as never)
                  }
                  error={errors[area.key]} />);


            })}
          </div>
        </section>);


    case 'gantt':
      return (
        <section className="card">
          <div className="card-body">
            <p className="card-subtitle" style={{ marginBottom: 'var(--space-4)' }}>
              Please add the activities to be conducted and check the corresponding timing
              by clicking the appropriate cell(s). Overlap is expected (i.e. more than one
              activity in certain months).
            </p>
            <GanttTable
              activities={data.gantt}
              monthCount={ganttMonthCount(data.durationMonths)}
              onChange={(gantt) => update({ gantt })}
            />
            <InlineError message={errors.gantt} />
          </div>
        </section>);


    case 'grant':
      return (
        <section className="card">
          <div className="card-body">
            <div className="notice" style={{ marginBottom: 'var(--space-5)' }}>
              <MailIcon size={18} aria-hidden="true" />
              <span>
                Grant requests are reviewed by the Research Advisory &amp; Grant Review
                Committee, {INSTITUTION}. Queries:{' '}
                <a href={`mailto:${irbEmail}`}>{irbEmail}</a>.
              </span>
            </div>

            <div className="field">
              <span className="field-label" id="grant-label">
                Grant Required from SMDC?
                <span className="req" aria-hidden="true">*</span>
              </span>
              <div className="choice-row" role="radiogroup" aria-labelledby="grant-label">
                {(['yes', 'no'] as const).map((option) =>
                <label
                  key={option}
                  className={`choice${data.grantRequired === option ? ' choice--active' : ''}`}>
                  
                    <input
                    type="radio"
                    name="grantRequired"
                    value={option}
                    checked={data.grantRequired === option}
                    onChange={() => update({ grantRequired: option })} />
                  
                    {option === 'yes' ? 'Yes' : 'No'}
                  </label>
                )}
              </div>
              <InlineError message={errors.grantRequired} />
            </div>

            {data.grantRequired === 'yes' ?
            <div style={{ marginTop: 'var(--space-4)', maxWidth: 360 }}>
                <TextField
                id="grantAmount"
                label="Amount requested (PKR)"
                required
                type="number"
                min="0"
                value={data.grantAmount}
                onChange={(v) => update({ grantAmount: v })}
                error={errors.grantAmount} />
              
              </div> :
            null}
          </div>
        </section>);


    case 'principalInvestigator':
      return (
        <section className="card">
          <div className="card-body">
            <div className="grid-2">
              <TextField
                id="piName"
                label="Name"
                required
                value={data.piName}
                onChange={(v) => update({ piName: v })}
                error={errors.piName} />
              
              <TextField
                id="piTitle"
                label="Title / Position"
                required
                value={data.piTitle}
                onChange={(v) => update({ piTitle: v })}
                error={errors.piTitle} />
              
              <TextField
                id="piInstitution"
                label="Institution"
                required
                value={data.piInstitution}
                onChange={(v) => update({ piInstitution: v })}
                error={errors.piInstitution} />
              
              <TextField
                id="piContact"
                label="Contact no."
                required
                type="tel"
                value={data.piContact}
                onChange={(v) => update({ piContact: v })}
                error={errors.piContact} />
              
              <TextField
                id="piEmail"
                label="Email"
                required
                type="email"
                value={data.piEmail}
                onChange={(v) => update({ piEmail: v })}
                error={errors.piEmail} />
              
            </div>
          </div>
        </section>);


    case 'coInvestigators':
      return (
        <section className="stack">
          <CoInvestigators
            items={data.coInvestigators}
            onChange={(coInvestigators) => update({ coInvestigators })} />
          
          <InlineError message={errors.coInvestigators} />
        </section>);


    case 'endorsements':
      return (
        <section className="card">
          <div className="card-body">
            <div className="grid-2">
              <TextField
                id="hodName"
                label="Name of Head of Department / Institute / Unit"
                value={data.hodName}
                onChange={(v) => update({ hodName: v })}
                error={errors.hodName} />
              
              <TextField
                id="supervisorName"
                label="Name of Supervisor"
                value={data.supervisorName}
                onChange={(v) => update({ supervisorName: v })} />
              
            </div>

            <hr className="divider" />

            <div className="declaration">
              It will be the responsibility of signatories for the scientific and ethical
              conduct of the proposed research and submission of progress reports if this
              application is approved.
            </div>
          </div>
        </section>);


    case 'documents':
      return (
        <section className="stack">
          {data.coverLetter.required ?
          <CoverLetterUpload error={errors.coverLetter} /> :
          null}

          <div className="card">
            <div className="card-header">
              <div>
                <h2 className="card-title">Associated Documents</h2>
                <p className="card-subtitle">
                  Please upload the required &amp; related documents of your research
                  project. File size of each file to be uploaded, should be less than{' '}
                  {config?.maxDocumentMB ?? 1} MB. Documents should be uploaded in{' '}
                  <strong>.pdf</strong> format only.
                </p>
              </div>
            </div>
            <div className="card-body">
              <DocumentsTable showRequiredError={Boolean(errors.documents)} />

              <InlineError message={errors.documents} />
            </div>
          </div>
        </section>);


    case 'declaration':
      return (
        <section className="card">
          <div className="card-body">
            <div className="declaration">
              I declare that all information provided by me in this Research Proposal is
              accurate, complete and true. I understand that any inaccurate, incomplete or
              false information given or any omission of information required shall render
              this application invalid. I acknowledge that IRB Secretariat may reverse its
              decision about proceeding this proposal due to incorrect or incomplete
              information provided by myself.
            </div>

            <label
              className="checkbox-inline"
              htmlFor="agree"
              style={{ marginTop: 'var(--space-4)' }}>
              
              <input
                id="agree"
                type="checkbox"
                checked={data.agreed}
                onChange={(e) => update({ agreed: e.target.checked })} />
              
              <span>I agree</span>
            </label>
            <InlineError message={errors.agreed} />
          </div>
        </section>);


    default:
      return null;
  }
}